import { Form, Input, InputNumber } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/styles';

import i18n from '../../assets/languages/configs';
import listCountry from '../../api/data-dummy/list-country.json';
import CustomSelect from '../CustomSelect/CustomSelect';
import CustomAntButton from '../CustomAntButton/CustomAntButton';
import { getValueByLanguage } from '../../utils';
import './style.css';
import styles from './styles.module.css';

const { TextArea } = Input;

interface UserInfoFormProps {
    formFields: any[];
    initialValues: any;
    onFormSubmit: (request: any) => Promise<void>;
}

const UserInfoForm = ({ formFields = [], initialValues = {}, onFormSubmit = async () => {} }: UserInfoFormProps) => {
    const [countryWithPhone, setCountryWithPhone] = useState<{
        value?: string;
        dialCode?: string;
        locale?: string;
        name?: string;
        label?: React.ReactNode;
    }>({
        dialCode: '+81',
        locale: 'jp',
        label: (
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '5px' }}>
                <div style={{ marginRight: '5px' }} className={`iti__flag iti__jp`}></div>
                <span>{'+81'}</span>
            </div>
        ),
    });
    // const [isConFirmChecked, setIsConFirmChecked] = useState(false);

    const { language } = i18n;
    const l = useCallback((text: string) => getValueByLanguage(text, language), [language]);
    const { t } = useTranslation();
    const [form] = Form.useForm();
    let isComposing = false;
    // const { user } = useFujiAuth();
    // const { userId } = user || {};

    useEffect(() => {
        form.setFieldsValue(initialValues);
        const initialValuesCountry = initialValues.countryCode;
        if (initialValuesCountry) {
            setCountryWithPhone({
                value: initialValuesCountry.value,
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '5px' }}>
                        <div
                            style={{ marginRight: '5px' }}
                            className={`iti__flag iti__${initialValuesCountry.locale}`}
                        ></div>
                        <span>{initialValuesCountry.dialCode}</span>
                    </div>
                ),
                dialCode: initialValuesCountry.dialCode,
                locale: initialValuesCountry.locale,
                name: initialValuesCountry.name,
            });
        }
    }, [initialValues, form]);

    const { countryOptions, countryOptionsWithPhone } = useMemo(() => {
        const countryData = intlTelInput.getCountryData() ?? [];
        const newData = countryData.reduce((acc: any, item: any) => {
            if (item.areaCodes && item.areaCodes.length > 0) {
                item.areaCodes.forEach((areaCode: any, index: any) => {
                    acc.push({
                        ...item,
                        dialCode: `+${item.dialCode}-${areaCode}`,
                        value: `${item.dialCode}-${areaCode}${item.iso2}${index}`,
                    });
                });
            } else {
                acc.push({
                    ...item,
                    dialCode: `+${item.dialCode}`,
                    value: `${item.dialCode}-${item.iso2}`,
                });
            }
            return acc;
        }, []);
        const countryOptionsWithPhone = newData.map((countryItem: any) => {
            return {
                value: countryItem.value,
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '5px' }}>
                        <div style={{ marginRight: '5px' }} className={`iti__flag iti__${countryItem.iso2}`}></div>
                        <span>{countryItem.dialCode}</span>
                    </div>
                ),
                dialCode: countryItem.dialCode,
                locale: countryItem.iso2,
                name: countryItem.name,
            };
        });
        const countryOptions = listCountry.map((countryItem: string) => {
            return {
                value: countryItem,
                label: countryItem,
            };
        });
        return {
            countryOptionsWithPhone,
            countryOptions,
        };
    }, []);

    const handleSubmit = (formValue: Record<string, any>) => {
        const request = { ...formValue };
        if (formValue.phoneNumber) {
            request.countryCode = { ...countryWithPhone, label: null };
        }
        onFormSubmit(request)?.catch((error: any) => {
            if (error.errorType === 'not_match_voucher_code' || error.errorType === 'disabled_voucher_code') {
                const field = formFields.find((field) => field.key === 'voucherCode');
                const message = field?.validator?.rules?.find((rule: any) => rule.type === 'notMatchValue')?.message;
                form.setFields([
                    {
                        name: ['voucherCode'],
                        errors: [l(message)],
                        touched: true,
                        value: null,
                    },
                ]);
                form.scrollToField('voucherCode');
                form.getFieldInstance('voucherCode').focus();
            }
        });
    };

    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
        if (!isComposing) {
            const valueIsNumber = typeof e === 'number';
            let inputValue = valueIsNumber ? e : e?.target?.value;
            const hasValue = inputValue !== null && inputValue !== undefined && inputValue !== '';

            if (field?.replace && field?.type === 'text' && hasValue) {
                field?.replace?.forEach(
                    ({ searchValue, replaceValue }: { searchValue: string; replaceValue: string }) => {
                        const regex = new RegExp(searchValue, 'g');
                        inputValue = String(inputValue).replace(regex, replaceValue);
                        form.setFieldValue(field.key, inputValue);
                    },
                );
            }
            if (field.key === 'voucherCode' && hasValue) {
                form.setFieldValue('voucherCode', String(inputValue).toUpperCase());
            }
            void form.validateFields([field.key]);
        }
    };

    const handleCompositionStart = () => {
        isComposing = true;
    };

    const handleCompositionEnd = (event: React.ChangeEvent<HTMLInputElement>, field: any) => {
        isComposing = false;
        handleValueChange(event, field);
    };

    return (
        <Form
            form={form}
            className={styles.registerForm}
            initialValues={initialValues}
            onFinish={handleSubmit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                }
            }}
            autoComplete="off"
        >
            {formFields.map((field) => {
                const fieldName = field.key;
                const label = l(field.label);
                const placeholder = l(field.placeholder);
                const customFieldRules =
                    field?.validator?.rules.map((item: any) => {
                        const rule = { ...item };
                        switch (rule?.type) {
                            case 'pattern':
                                rule.pattern = new RegExp(rule.pattern);
                                break;
                            case 'required':
                                rule.validator = (_: any, value: any) => {
                                    if (value !== null && value !== undefined && String(value).trim() !== '') {
                                        return Promise.resolve();
                                    } else {
                                        return Promise.reject();
                                    }
                                };
                                break;
                            case 'match':
                                field.dependencies = rule.matchKey;
                                return ({ getFieldValue }: { getFieldValue: (params: any) => any }) => ({
                                    validator(_: any, value: any) {
                                        if (getFieldValue(rule.matchKey) === value) {
                                            return Promise.resolve();
                                        } else {
                                            return Promise.reject();
                                        }
                                    },
                                    message: l(rule.message),
                                });

                            default:
                                break;
                        }
                        return { ...rule, message: l(rule.message) };
                    }) ?? [];

                switch (field.type) {
                    case 'number':
                        switch (fieldName) {
                            case 'phoneNumber':
                                return (
                                    <div key={fieldName}>
                                        <p className={styles.label}>{label}</p>
                                        <Form.Item name={fieldName} rules={[...customFieldRules]}>
                                            <Input
                                                addonBefore={
                                                    <div className="prefixPhoneNumber">
                                                        <CustomSelect
                                                            showSearch
                                                            listHeight={200}
                                                            size="large"
                                                            variant="plain"
                                                            popupMatchSelectWidth={false}
                                                            value={countryWithPhone}
                                                            className="codePhoneNumber"
                                                            options={countryOptionsWithPhone}
                                                            onChange={(_: any, option: any) =>
                                                                setCountryWithPhone(option)
                                                            }
                                                            getPopupContainer={(trigger: HTMLElement) =>
                                                                trigger.parentNode
                                                            }
                                                            filterOption={(input: any, option: any) => {
                                                                return (
                                                                    (option?.locale ?? '')
                                                                        .toLowerCase()
                                                                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                                                        .includes(input.toLowerCase()) ||
                                                                    (option?.name ?? '')
                                                                        .toLowerCase()
                                                                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                                                        .includes(input.toLowerCase()) ||
                                                                    (option?.dialCode ?? '')
                                                                        .toLowerCase()
                                                                        .includes(input.toLowerCase())
                                                                );
                                                            }}
                                                            optionFilterProp="children"
                                                        />
                                                    </div>
                                                }
                                                size="large"
                                                className={styles.input}
                                                placeholder={placeholder}
                                            />
                                        </Form.Item>
                                    </div>
                                );
                            default:
                                return (
                                    <div key={fieldName}>
                                        <p className={styles.label}>{label}</p>
                                        <Form.Item
                                            name={fieldName}
                                            rules={[...customFieldRules]}
                                            dependencies={[field.dependencies]}
                                        >
                                            <InputNumber
                                                placeholder={placeholder}
                                                onChange={(e) =>
                                                    handleValueChange(
                                                        e as unknown as React.ChangeEvent<HTMLInputElement>,
                                                        field,
                                                    )
                                                }
                                                onCompositionStart={handleCompositionStart}
                                                onCompositionEnd={(e) =>
                                                    handleCompositionEnd(
                                                        e as unknown as React.ChangeEvent<HTMLInputElement>,
                                                        field,
                                                    )
                                                }
                                            />
                                        </Form.Item>
                                    </div>
                                );
                        }

                    case 'enum':
                        switch (fieldName) {
                            case 'country':
                                return (
                                    <div key={fieldName}>
                                        <p className={styles.label}>{label}</p>
                                        <Form.Item name={fieldName} rules={[...customFieldRules]}>
                                            <CustomSelect
                                                variant="plain"
                                                size="large"
                                                showSearch
                                                placeholder={placeholder}
                                                options={countryOptions}
                                                className="selectOption"
                                                filterOption={(input: string, option: any) => {
                                                    return (option?.value ?? '')
                                                        .toLowerCase()
                                                        .includes(input.toLowerCase());
                                                }}
                                                getPopupContainer={(trigger: HTMLElement) => trigger.parentNode}
                                            />
                                        </Form.Item>
                                    </div>
                                );

                            default:
                                return (
                                    <div key={fieldName}>
                                        <p className={styles.label}>{label}</p>
                                        <Form.Item name={fieldName} rules={[...customFieldRules]}>
                                            <CustomSelect
                                                variant="plain"
                                                size="large"
                                                placeholder={placeholder}
                                                options={field?.options?.map((field: any) => {
                                                    const label = l(field);
                                                    const value = field.optionKey
                                                        ? `option=${field.optionKey}`
                                                        : l(field);
                                                    return {
                                                        label,
                                                        value,
                                                    };
                                                })}
                                                className="selectOption"
                                                getPopupContainer={(trigger: HTMLElement) => trigger.parentNode}
                                            />
                                        </Form.Item>
                                    </div>
                                );
                        }

                    case 'textarea':
                        return (
                            <div key={fieldName}>
                                <p className={styles.label}>{label}</p>
                                <Form.Item
                                    name={fieldName}
                                    rules={[...customFieldRules]}
                                    dependencies={[field.dependencies]}
                                >
                                    <TextArea
                                        rows={3}
                                        size="large"
                                        className={styles.input}
                                        placeholder={placeholder}
                                        onChange={(e) =>
                                            handleValueChange(
                                                e as unknown as React.ChangeEvent<HTMLInputElement>,
                                                field,
                                            )
                                        }
                                        onCompositionStart={handleCompositionStart}
                                        onCompositionEnd={(e) =>
                                            handleCompositionEnd(
                                                e as unknown as React.ChangeEvent<HTMLInputElement>,
                                                field,
                                            )
                                        }
                                    />
                                </Form.Item>
                            </div>
                        );

                    default:
                        return (
                            <div key={fieldName}>
                                <p className={styles.label}>{label}</p>
                                <Form.Item
                                    name={fieldName}
                                    rules={[...customFieldRules]}
                                    dependencies={[field.dependencies]}
                                >
                                    <Input
                                        size="large"
                                        className={styles.input}
                                        placeholder={placeholder}
                                        onChange={(e) => handleValueChange(e, field)}
                                        onCompositionStart={handleCompositionStart}
                                        onCompositionEnd={(e) =>
                                            handleCompositionEnd(
                                                e as unknown as React.ChangeEvent<HTMLInputElement>,
                                                field,
                                            )
                                        }
                                    />
                                </Form.Item>
                            </div>
                        );
                }
            })}
            {/* {userId && (
                <div>
                    <div className={styles.confirmBox}>
                        <Checkbox
                            onChange={() => setIsConFirmChecked((preState) => !preState)}
                            className={styles.checkbox}
                        />
                        <label className={styles.checkboxLabel}>{t('reservation.save')}</label>
                    </div>
                    {isConFirmChecked && <div className={styles.confirmNote}>{t('reservation.confirm-note')}</div>}
                </div>
            )} */}
            <Form.Item>
                <CustomAntButton type="outlined" block htmlType="submit" className={styles.submitBtn}>
                    {t('reservation.next-step')}
                </CustomAntButton>
            </Form.Item>
        </Form>
    );
};

export default UserInfoForm;
